import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const pad = (n) => String(Math.floor(n)).padStart(2, '0');
const formatTime = (totalSec) => {
  if (!isFinite(totalSec) || totalSec <= 0) return '—';
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = Math.round(totalSec % 60);
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};
const formatPace = (secPerKm) => {
  if (!isFinite(secPerKm) || secPerKm <= 0) return '—';
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${pad(s)} /km`;
};

const PaceCalculator = () => {
  const [mode, setMode] = useState('pace');
  const [distance, setDistance] = useState(5);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(28);
  const [seconds, setSeconds] = useState(30);
  const [paceMin, setPaceMin] = useState(5);
  const [paceSec, setPaceSec] = useState(30);

  const result = useMemo(() => {
    if (mode === 'pace') {
      const totalSec = hours * 3600 + minutes * 60 + seconds;
      const secPerKm = distance > 0 ? totalSec / distance : 0;
      const speedKph = totalSec > 0 ? (distance / totalSec) * 3600 : 0;
      return { secPerKm, speedKph, totalSec };
    }
    // mode time
    const secPerKm = paceMin * 60 + paceSec;
    const totalSec = secPerKm * distance;
    const speedKph = secPerKm > 0 ? 3600 / secPerKm : 0;
    return { secPerKm, speedKph, totalSec };
  }, [mode, distance, hours, minutes, seconds, paceMin, paceSec]);

  return (
    <CalculatorLayout
      title="Pace Calculator"
      subtitle="Calculate running pace, speed, and finish time for any distance."
      category="Fitness"
      description={<p>Plan your run, walk, or cycle workout. Switch between calculating pace from a known time, or projecting time from a target pace. Includes preset distances for 5K, 10K, half-marathon, and full marathon.</p>}
      formulaNote="Pace (min/km) = Time / Distance   •  Speed (km/h) = Distance / Time × 60"
    >
      <Tabs value={mode} onValueChange={setMode} className="mb-5">
        <TabsList>
          <TabsTrigger value="pace">Calculate Pace</TabsTrigger>
          <TabsTrigger value="time">Calculate Time</TabsTrigger>
        </TabsList>
        <TabsContent value="pace" /><TabsContent value="time" />
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <SliderInput label="Distance" unit="km" value={distance} onChange={setDistance} min={0.1} max={100} step={0.1} />
          <div className="flex gap-2 flex-wrap">
            {[{ l: '5K', v: 5 }, { l: '10K', v: 10 }, { l: 'Half', v: 21.0975 }, { l: 'Full', v: 42.195 }].map(p => (
              <button key={p.l} onClick={() => setDistance(p.v)} className="px-3 py-1.5 rounded-md text-xs border border-slate-200 hover:bg-saffron-50 hover:border-saffron-200">{p.l} ({p.v}km)</button>
            ))}
          </div>

          {mode === 'pace' ? (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Time</label>
              <div className="grid grid-cols-3 gap-2">
                <SliderInput label="Hours" value={hours} onChange={setHours} min={0} max={10} step={1} />
                <SliderInput label="Minutes" value={minutes} onChange={setMinutes} min={0} max={59} step={1} />
                <SliderInput label="Seconds" value={seconds} onChange={setSeconds} min={0} max={59} step={1} />
              </div>
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Pace (per km)</label>
              <div className="grid grid-cols-2 gap-2">
                <SliderInput label="Min" value={paceMin} onChange={setPaceMin} min={2} max={15} step={1} />
                <SliderInput label="Sec" value={paceSec} onChange={setPaceSec} min={0} max={59} step={1} />
              </div>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5 space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pace</div>
            <div className="font-display text-3xl font-bold text-saffron-700 display-font mt-1">{formatPace(result.secPerKm)}</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Speed</div>
            <div className="font-display text-3xl font-bold text-indgreen-700 display-font mt-1">{result.speedKph.toFixed(2)} km/h</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Time</div>
            <div className="font-display text-3xl font-bold text-bharat-navy display-font mt-1">{formatTime(result.totalSec)}</div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

export default PaceCalculator;
